/**
 * World 6 — "Champion Road" (All). Narrative fragment for campaignStories.ts.
 * THIS OPENS ACT II — THE FIRST KEEPER. Dialogue voiced at Pokémon Red trainer
 * complexity.
 *
 * THE TALE (Act II, chapter 1): with all five Act I Guardians remembered, the old
 * Keepers' road opens — and this is THE REVEAL. Post-split, Mimi and Lulu walk it
 * on separate marker stones; Clan Z's Zip and Zap still work the road, their branded
 * beasts nearly full grown now; Aggie confesses at the library wall that Old Man Gray is the First Keeper
 * is the Soul-Eater, and that nineteen was never the total — it's what's left
 * after a thousand years of eating. Timmy, near-equal now, isn't speaking to
 * Aggie. The road ends at the hollow echo of the First Champ, won by Timmy's
 * belief. Squads are mixed-element (champions of every kind), so this world's
 * `element` is 'All' and the difficulty test relaxes its per-element check.
 */

import type { NodeStory } from '../../campaignStories.js';

export const WORLD6_TAGLINE =
  "Five Guardians trust you. The old champions' road is open. Aggie has stopped answering your questions, and Timmy has noticed.";

export const WORLD6_ZONE_STORIES: Record<string, string> = {
  z15: "The old Keepers' road is open. Your friends walk it too, but not side by side anymore.",
  z16: "Past the statues lies a burned library. Aggie is waiting with her secret.",
  z17: "Gray is heading for the island's edge. The old Keepers gather along the path to him.",
};

export const WORLD6_NODE_STORIES: Record<string, NodeStory> = {
  // --- Zone 15: Hero Road (1v1) ---
  "z15-n1": {
    story: "At the first marker stone, Mimi is waiting for you. Alone.",
    dialogue: {
      intro: "Lulu took the next marker stone. So this victory will be entirely mine. Probably. Ready?",
      playerWin: "No fair! I turned to complain to Lulu. She's not here. That's weird.",
      playerLose: "I win! No twin to share the glory!",
      introRematchAfterLoss: "Back? Good. I could use the company.",
      introRematchAfterWin: "Rematch? I've been practising my victory speech. Lulu usually edits it.",
      playerWinRepeat: "You win. Tell Lulu it was close. Close enough.",
      playerLoseRepeat: "Another win. I wish she were here to complain about my cheering.",
    },
  },
  "z15-n2": {
    story: "At the next marker stone, Lulu waits for you. She keeps glancing back.",
    dialogue: {
      intro: "Mimi got her match. I want mine.",
      playerWin: "Good match. Tell Mimi I'm doing fine. You can leave out the losing.",
      playerLose: "I won. No one to grin with. Go.",
      introRematchAfterLoss: "Back. Still just me. Again.",
      introRematchAfterWin: "Another round? I could use something to think about besides my sister.",
      playerWinRepeat: "You win. I nearly turned to tell her again.",
      playerLoseRepeat: "Another win. It's strange without her shouting over it.",
    },
  },
  "z15-n3": {
    story: "Dash fills the road in front of you from statue to statue. Behind him, Zip guards a caged shard.",
    dialogue: {
      intro: "Champion Road! Dash blocks it all by himself now. I brought backup anyway. Don't let this one through!",
      playerIntro: "Dash, take a seat. This won't be long.",
      playerWin: "No no no! The cage popped open! Our Talion shard flew home to you. That's eleven! Zeck can do his own paperwork!",
      playerLose: "Ha! Even here, Clan Z wins!",
      introRematchAfterLoss: "Back on my road? Dash is warmed up!",
      introRematchAfterWin: "I'm not putting another loss in my report. Dash, help me out!",
      playerWinRepeat: "Fine! Zeck can guard his own cages!",
      playerLoseRepeat: "Slowpoke! Clan Z rules the last road!",
    },
  },
  "z15-nw1": {
    story: "The road gets hot under your feet. A wild Zaricat guards this stretch.",
    dialogue: {
      intro: "A wild Zaricat springs up! It won't let you pass!",
      playerWin: "The wild Zaricat bounds off up the smoking road.",
      playerLose: "The wild Zaricat drives you back down the road.",
      introRematchAfterLoss: "The road heats up. The wild Zaricat returns.",
      introRematchAfterWin: "The Zaricat knows your scent now. It bares its claws.",
      playerWinRepeat: "The wild Zaricat bolts into the far heat.",
      playerLoseRepeat: "The pass stays wild. The road runs hot.",
    },
  },
  "z15-n4": {
    story: "Cliff waits for you at the last marker stone before Keeper Hall. His Ray, Thistle, keeps watch from the stone.",
    dialogue: {
      intro: "The hall is ahead. The island's ranked champions all trained on this road. One last drill.",
      playerWin: "You're ready. I'll keep the road clear behind you.",
      playerLose: "A bit more practice. Sentry's in no hurry, and neither am I.",
      introRematchAfterLoss: "Back. Sentry never left her post. Neither did I.",
      introRematchAfterWin: "Sentry spotted you before I did. I think she's still watching you.",
      playerWinRepeat: "Good match. Sentry approves. That's why she hasn't pecked you.",
      playerLoseRepeat: "The road holds because someone has to.",
    },
  },

  // --- Zone 16: Keeper Hall (2v2) ---
  "z16-n1": {
    story: "Among the statues, Vera guards the library door. She smiles when she sees you.",
    dialogue: {
      intro: "You've come a long way since the thorn gate. Let me see just how far.",
      playerWin: "The spirits here went quiet for you. Go on in. You won't face it alone.",
      playerLose: "The spirits want another look at you. Rest first.",
      introRematchAfterLoss: "Back. The library isn't going anywhere. Neither am I.",
      introRematchAfterWin: "Another match before you go? I could use the distraction too.",
      playerWinRepeat: "You win. The library door is open.",
      playerLoseRepeat: "I've still got a few surprises. Come back for another round.",
    },
  },
  "z16-n2": {
    story: "Aggie holds your {partner}'s shard close. Two letters glow inside it. Timmy won't look at her.",
    dialogue: {
      intro: "That writing was my family's. One more battle, child. Then no more hiding.",
      playerWin: "Gray was the first Keeper of our family. Everyone forgot him, and I kept quiet.",
      playerLose: "We'll try again. You deserve the truth, even if I'm scared to say it.",
      introRematchAfterLoss: "Those letters haven't changed. Neither has what I did.",
      introRematchAfterWin: "Ask again if you need it twice. I'll keep saying it.",
      playerWinRepeat: "I can't say his name alone. I can help you find him. If you'll let me.",
      playerLoseRepeat: "Rest a moment. I'm not going to disappear before you can ask me again.",
    },
  },
  "z16-n3": {
    story: "Timmy blocks your way, fists tight. Chirpy presses against his cheek. He still isn't speaking to Aggie.",
    dialogue: {
      intro: "I don't want to talk about Aggie. I want a battle. A proper one.",
      playerWin: "You win. I didn't even count. That's how mad I am.",
      playerLose: "We won. I'm still angry. But that helped a bit.",
      introRematchAfterLoss: "Back? Good. Beats thinking about those letters.",
      introRematchAfterWin: "Another round. I can think about Aggie later.",
      playerWinRepeat: "You win. I'm still not ready to talk to her.",
      playerLoseRepeat: "A win. Chirpy's pleased, anyway.",
    },
  },
  "z16-n5": {
    story: "Zap is boxing up the hall for Zeck. Above you, Shade perches on a giant statue like a bird on a post.",
    dialogue: {
      intro: "The statues used to look down on Shade. Now he looks them in the eye. Zeck wants this hall boxed up. First, you.",
      playerIntro: "Nobody boxes up this hall.",
      playerWin: "There go the cages. Zeck will hear about this.",
      playerLose: "Quiet wins. Clan Z keeps the hall.",
      introRematchAfterLoss: "Back in the hall. Shade watched you. Ready.",
      introRematchAfterWin: "Zeck keeps asking about you. I want a new answer.",
      playerWinRepeat: "You win. Zeck still holds the keys. Go on.",
      playerLoseRepeat: "The hall stays shut. Clan Z holds it.",
    },
  },
  "z16-nw1": {
    story: "A heavy wild pair moves toward you between the stone Keepers.",
    dialogue: {
      intro: "A wild Drago rears in the hall! A Gorly lumbers up behind it!",
      playerWin: "The wild pair retreats past the stone Keepers.",
      playerLose: "The wild pair drives you from the hall.",
      introRematchAfterLoss: "The hall rumbles. The wild pair returns.",
      introRematchAfterWin: "The pair knows your squad now. Their eyes glow.",
      playerWinRepeat: "The wild pair stamps off into the dark.",
      playerLoseRepeat: "The hall stays wild. The floor still shakes.",
    },
  },
  "z16-n4": {
    story: "At the hall's great doors, a stranger waits for you, arms open.",
    dialogue: {
      intro: "Mirror Isle copied my face long ago. Now I have it back. Show me you kept yours.",
      playerWin: "Still you. Still looking after them. Gray needs someone like that.",
      playerLose: "You blinked first. Trust yourself, then come back.",
    },
  },

  // --- Zone 17: Far Marker (3v3) ---
  "z17-n1": {
    story: "The old Keepers make way for you. One stays.",
    dialogue: {
      intro: "We tried to bring him home. None of us managed it. Let's see your team.",
      playerWin: "You have good friends beside you. Take them with you. We went alone.",
      playerLose: "Then we wait, as we always have.",
    },
  },
  "z17-n2": {
    story: "A traveller in a dusty coat stands in your path, holding out empty hands.",
    dialogue: {
      intro: "I came to help Gray. Now when people call my name, I don't turn round. Do you?",
      playerWin: "When he runs, follow. I wish someone had followed me.",
      playerLose: "Come back when you're ready. I came too soon.",
    },
  },
  "z17-n3": {
    story: "On the last step, a gray songbird blocks your way, singing for someone lost.",
    dialogue: {
      intro: "Everyone stopped looking for him. You will too. Turn back.",
      playerWin: "You stay. The gray songbird folds its wings and sings one soft note.",
      playerLose: "Then give up on him too. It's easier.",
    },
  },
  "z17-n4": {
    story: "Your road ends in a ring. Three rounds. The Champ, a little wizard Sumiya, casts a spell, and the statue above Timmy cracks.",
    dialogue: {
      intro: "The Sumiya by the statue waited years for its Keeper. Now it growls even at Gray. It thinks it belongs to nobody.",
      playerWin: "You stop the fight to pull Timmy clear. \"You're SOMEBODY'S champion!\" he yells. The Champ bows and gives you its shard. That's twelve!",
      playerLose: "In the top row, Gray looks thinner and fainter.",
    },
  },
};
